/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import React from 'react';
import { DashboardTab } from './DashboardPanel';
import { DASHBOARD_TABS, getDashboardTabMeta } from './dashboardTabMeta';
import { nav, buttons, type, cn } from './kit';

interface TabNavigationProps {
  activeTab: DashboardTab;
  onTabChange: (tab: DashboardTab) => void;
  isMobile: boolean;
  onClose?: () => void;
  onExitToMenu?: () => void;
}

const TabNavigation: React.FC<TabNavigationProps> = ({ activeTab, onTabChange, isMobile, onClose, onExitToMenu }) => {
  const activeMeta = getDashboardTabMeta(activeTab);

  if (isMobile) {
    return (
      <div className="bg-black border-b border-white/10">
        <div className="px-2 py-3">
          <div className="flex gap-1 overflow-x-auto no-scrollbar pb-1">
            {DASHBOARD_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => onTabChange(tab.id)}
                title={`${tab.enables} — ${tab.value}`}
                className={cn(
                  'flex flex-col items-center justify-center min-w-[64px] px-2 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap border',
                  activeTab === tab.id ? nav.itemActive : nav.itemIdle,
                )}
              >
                <span className="text-lg mb-0.5">{tab.icon}</span>
                <span>{tab.shortLabel}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="px-3 pb-3 border-t border-white/5 bg-slate-950/50">
          <p className={cn(type.eyebrowAccent, 'normal-case tracking-wide text-cyan-400/90')}>
            {activeMeta.icon} {activeMeta.label}
          </p>
          <p className={cn(type.caption, 'text-[10px] mt-0.5 leading-snug')}>{activeMeta.enables}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-56 lg:w-60 bg-black border-r border-white/10 p-4 flex flex-col gap-1 shrink-0">
      <div className="mb-4 px-2">
        <h2 className="text-lg font-black text-cyan-400 tracking-widest uppercase leading-tight">
          Command<br />Center
        </h2>
        <p className={cn(type.caption, 'text-[9px] mt-2 leading-relaxed text-slate-500')}>
          Each section unlocks different controls. Pick based on what you need to fix next.
        </p>
      </div>

      {DASHBOARD_TABS.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onTabChange(tab.id)}
            title={tab.value}
            className={cn(nav.item, 'group relative', isActive ? nav.itemActive : nav.itemIdle)}
          >
            <span className="font-bold uppercase tracking-widest flex items-center gap-2">
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </span>
            <span className={cn(nav.itemSub, isActive ? nav.itemSubActive : nav.itemSubIdle)}>
              {tab.enables}
            </span>
          </button>
        );
      })}

      <div className="mt-auto pt-4 flex flex-col gap-3 border-t border-white/10">
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className={cn(buttons.resume, 'w-full flex items-center justify-center gap-2 hover:shadow-emerald-500/25')}
          >
            <span>▶</span> Resume Game
          </button>
        )}
        {onExitToMenu && (
          <button
            type="button"
            onClick={() => {
              if (window.confirm('Abort current simulation and return to main menu?')) {
                onExitToMenu();
              }
            }}
            className={cn(buttons.danger, 'w-full')}
          >
            Quit to Menu
          </button>
        )}
      </div>
    </div>
  );
};

export default TabNavigation;